import { TextField, TextFieldProps } from '@mui/material';
import { ChangeEvent, useState } from 'react';

import { FieldMetaData } from './types';

type Sa1TFCProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  onChange: (value: number[], fieldMetaData: FieldMetaData) => void;
  /** Comma-separated SA1 numbers for Restart pre-fill */
  initialValue?: number[];
};

export default function Sa1TFC({ onChange, initialValue, ...props }: Sa1TFCProps) {
  const [value, setValue] = useState(
    initialValue?.length ? initialValue.join(', ') : ''
  );
  const [isError, setIsError] = useState(false);

  const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let newValue = event.target.value;
    newValue = newValue.replace(/[^0-9,\s]/, '');
    setValue(newValue);

    const { arrVal, hasError } = parseSa1s(newValue);
    setIsError(hasError);

    let fieldMetaData: FieldMetaData;
    if (hasError) {
      fieldMetaData = {
        state: 'invalid',
        error: {
          reason: 'invalid',
          helperText:
            'Please enter only 11 digit numbers and use commas to separate multiple listed numbers',
        },
      };
    } else {
      fieldMetaData = { state: arrVal.length === 0 ? 'empty' : 'valid', error: null };
    }

    onChange(arrVal, fieldMetaData);
  };

  return (
    <TextField
      value={value}
      onChange={handleOnChange}
      type="text"
      variant="filled"
      error={isError}
      color="secondary"
      {...props}
    />
  );
}

const validSa1Regex = /^\d{11}$/;

function parseSa1s(strValue: string): { arrVal: number[]; hasError: boolean } {
  const trimmed = strValue.trim();
  if (trimmed === '') {
    return { arrVal: [], hasError: false };
  }

  const valueArray = trimmed.split(',') ?? [];

  if (valueArray.length === 0) {
    return { arrVal: [], hasError: false };
  }

  const trimmedArray = valueArray.map((n) => n.trim());

  const arrVal: number[] = [];
  for (const n of trimmedArray) {
    const trimmed = n.trim();
    if (validSa1Regex.test(trimmed)) {
      arrVal.push(parseInt(trimmed));
    } else {
      return { arrVal: [], hasError: true };
    }
  }

  return { arrVal, hasError: false };
}
